import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from './AuthContext'
import { getSubscriptionStatus, getMyAggregatorMemberships } from '../services/api'
import type { AggregatorMembership } from '../types'

interface AggregatorContextType {
  isAggregator: boolean
  hasAggregatorAccess: boolean
  aggregatorId: string | null
  aggregatorTier: string | null
  entityCount: number
  memberships: AggregatorMembership[]
  activeAggregatorId: string | null
  setActiveAggregatorId: (id: string | null) => void
  loading: boolean
  error: Error | null
}

const AggregatorContext = createContext<AggregatorContextType | undefined>(undefined)
const STORAGE_KEY = 'activeAggregatorId'
// BUG-136 / BUG-W10-02: record WHICH user owns the persisted aggregator. This
// lets us tell a same-user page RELOAD (keep the selection) apart from a
// cross-user login on a shared browser (clear it) — the two are otherwise
// indistinguishable at mount and the old code conflated them.
const OWNER_KEY = 'activeAggregatorOwner'

export function AggregatorProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth()
  const currentUserId = isAuthenticated ? user?.id ?? null : null
  const [activeAggregatorId, setActiveAggregatorIdState] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY)
  )

  const setActiveAggregatorId = (id: string | null) => {
    setActiveAggregatorIdState(id)
    if (id) {
      localStorage.setItem(STORAGE_KEY, id)
      // Stamp the owner so a later cross-user login can detect the staleness.
      if (currentUserId) localStorage.setItem(OWNER_KEY, currentUserId)
    } else {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(OWNER_KEY)
    }
  }

  // BUG-W10-02: clear a persisted aggregator that belongs to a DIFFERENT user (a
  // value leaked past logout on a shared browser) BEFORE memberships load, so
  // the axios interceptor — which reads localStorage on every request — never
  // sends a cross-user X-Aggregator-Id. BUG-136: a value owned by the CURRENT
  // user survives a page reload (do NOT clear it); the previous logic cleared on
  // every reload and reset a multi-aggregator analyst's choice. A legacy value
  // with no recorded owner is left to the W10-01 memberships-load validity check
  // below (it cannot belong to a user whose memberships don't contain it).
  const wasAuthedRef = useRef(false)
  useEffect(() => {
    const clearPersisted = () => {
      if (localStorage.getItem(STORAGE_KEY) !== null || activeAggregatorId !== null) {
        setActiveAggregatorIdState(null)
        localStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem(OWNER_KEY)
      }
    }
    if (!isAuthenticated) {
      // Clear on a genuine logout / 401 TRANSITION (was authenticated), not on
      // the initial pre-hydration render — otherwise a valid same-user value is
      // wiped before auth resolves.
      if (wasAuthedRef.current) clearPersisted()
      wasAuthedRef.current = false
      return
    }
    wasAuthedRef.current = true
    // Authenticated. Clear only a cross-user stale value (owner mismatch). When
    // the user object hasn't loaded yet (currentUserId null) we can't compare —
    // leave the value for the W10-01 memberships-validity check below.
    if (currentUserId !== null) {
      const owner = localStorage.getItem(OWNER_KEY)
      if (owner !== null && owner !== currentUserId) clearPersisted()
    }
  }, [isAuthenticated, currentUserId, activeAggregatorId]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: subData, isLoading: subLoading, error: subError } = useQuery({
    queryKey: ['subscription-status'],
    queryFn: getSubscriptionStatus,
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  })

  // isAggregator: caller OWNS an aggregator subscription (unchanged — 7 consumers depend on this)
  const isAggregator = subData?.tier?.startsWith('aggregator_') ?? false
  const aggregatorId = subData?.aggregator_id ?? null
  const aggregatorTier = subData?.tier ?? null
  const entityCount = subData?.entity_count ?? 0

  // Fetch all memberships from the backend (analysts + billing owners)
  const { data: membershipsData, isLoading: membershipsLoading, error: membershipsError } = useQuery({
    queryKey: ['my-aggregator-memberships'],
    queryFn: getMyAggregatorMemberships,
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  })

  const memberships: AggregatorMembership[] = membershipsData?.memberships ?? []

  // hasAggregatorAccess: true for billing owners (1 self-membership) AND analysts/admins
  const hasAggregatorAccess = memberships.length > 0

  // On membership load: clear a stale UUID that doesn't belong to the current user
  // (e.g. leftover from a previous user session on the same browser — BUG-W10-01),
  // then default to the first membership when nothing is stored. Gated on
  // isAuthenticated so a 401 auto-logout doesn't immediately re-promote a
  // stale value from the now-orphan React Query cache (BUG-W10-02).
  useEffect(() => {
    if (!isAuthenticated) return
    if (memberships.length === 0) return
    const validIds = new Set(memberships.map((m) => m.aggregator_id))
    if (activeAggregatorId && !validIds.has(activeAggregatorId)) {
      setActiveAggregatorId(memberships[0].aggregator_id)
    } else if (!activeAggregatorId) {
      setActiveAggregatorId(memberships[0].aggregator_id)
    }
  }, [isAuthenticated, memberships.length, activeAggregatorId]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AggregatorContext.Provider value={{
      isAggregator,
      hasAggregatorAccess,
      aggregatorId,
      aggregatorTier,
      entityCount,
      memberships,
      activeAggregatorId,
      setActiveAggregatorId,
      loading: subLoading || membershipsLoading,
      error: (subError || membershipsError) as Error | null,
    }}>
      {children}
    </AggregatorContext.Provider>
  )
}

export function useAggregator(): AggregatorContextType {
  const context = useContext(AggregatorContext)
  if (!context) throw new Error('useAggregator must be used within AggregatorProvider')
  return context
}
